import { ReactNode, useEffect, useMemo, useReducer } from 'react';
import { createContainer } from "unstated-next";
import { initTaxLegalCase, TaxLegalCase } from '../types/taxLegalTypes';

type TaxCaseStoreProps = {
  taxLegalCase: TaxLegalCase;
  actions: {
    resetState: () => void;
    setTaxLegalCase: (newTaxLegalCase: TaxLegalCase) => void;
    setVehicle: (vehicle: TaxLegalCase['vehicle']) => void;
    setEvaluation: (evaluation: TaxLegalCase['originalEvaluation']) => void;
    setFiles: (files: TaxLegalCase['files']) => void;
  }
}

const useTaxLegalCaseStore = (initialState?: TaxLegalCase): TaxCaseStoreProps => {
  const [taxLegalCase, dispatch] = useReducer(taxLegalCaseReducer, initialState || initTaxLegalCase);
  useEffect(() => {
    if (initialState) {
      dispatch({
        type: "SET_TAX_LEGAL_CASE", payload: initialState
      })
    }
  }, [initialState])

  return {
    taxLegalCase,
    actions: {
      resetState: () => dispatch({ type: "RESET", payload: initialState || initTaxLegalCase }),
      setTaxLegalCase: (newTaxLegalCase) => dispatch({ type: "SET_TAX_LEGAL_CASE", payload: newTaxLegalCase }),
      setVehicle: (vehicle) => dispatch({ type: "SET_VEHICLE", payload: vehicle }),
      setEvaluation: (evaluation) => dispatch({ type: "SET_EVALUATION", payload: evaluation }),
      setFiles: (files) => dispatch({ type: "SET_FILES", payload: files }),
    }
  };
};

const TaxLegalCaseStore = createContainer(useTaxLegalCaseStore);


type TaxLegalCaseStoreProviderProps = {
  children: ReactNode;
  initialState?: TaxLegalCase;
}
export function TaxLegalCaseStoreProvider({ children, initialState }: TaxLegalCaseStoreProviderProps) {
  return (
    <TaxLegalCaseStore.Provider initialState={initialState}>
      {children}
    </TaxLegalCaseStore.Provider>
  )
}

function useTaxLegalCaseSelector(): TaxLegalCase
function useTaxLegalCaseSelector<T>(selector: (state: TaxLegalCase) => T): T
function useTaxLegalCaseSelector<T>(selector?: (state: TaxLegalCase) => T) {
  const { taxLegalCase } = TaxLegalCaseStore.useContainer();
  // useMemo will come in hand if more states are introduced to useTaxLegalCaseStore
  return useMemo(() => {
    return selector ? selector(taxLegalCase) : taxLegalCase
  }, [taxLegalCase]);
};

export const useTaxLegalActions = () => TaxLegalCaseStore.useContainer().actions
export const useTaxLegalCase = () => useTaxLegalCaseSelector((state) => state);
export const useTaxLegalId = () => useTaxLegalCaseSelector((state) => state.id);
export const useTaxLegalStatus = () => useTaxLegalCaseSelector((state) => state.status);
export const useTaxLegalVehicle = () => useTaxLegalCaseSelector((state) => state.vehicle);
export const useTaxLegalEvaluation = () => useTaxLegalCaseSelector((state) => state.originalEvaluation);
export const useTaxLegalFiles = () => useTaxLegalCaseSelector((state) => state.files);

const taxLegalCaseReducer = (
  state: TaxLegalCase,
  action: TaxLegalCaseAction
): TaxLegalCase => {
  switch (action.type) {
    case "SET_VEHICLE":
      return { ...state, vehicle: action.payload };
    case "SET_EVALUATION":
      return { ...state, originalEvaluation: action.payload };
    case "SET_FILES":
      return { ...state, files: action.payload };
    case "SET_TAX_LEGAL_CASE":
      return action.payload;
    case "RESET":
      return action.payload; // Reset to a new state
    default:
      return state;
  }
};

type TaxLegalCaseAction =
  | { type: "SET_VEHICLE"; payload: TaxLegalCase['vehicle'] | null }
  | { type: "SET_EVALUATION"; payload: TaxLegalCase['originalEvaluation'] | null }
  | { type: "SET_FILES"; payload: TaxLegalCase['files'] }

  | { type: "SET_TAX_LEGAL_CASE"; payload: TaxLegalCase }
  | { type: "RESET"; payload: TaxLegalCase }; // For resetting the state
